import constants from 'utils/constants';

export interface ValidationRule {
  min?: number;
  max?: number;
  enum?: string[];
}

export interface FieldValidationSchema {
  required: string[];
  optional: string[];
  rules: Record<string, ValidationRule>;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  hasPartialData: boolean;
  hasAnyValue: boolean;
}

export interface FieldValidation {
  isRequired: boolean;
  isEmpty: boolean;
  showError: boolean;
  hasAnyValue: boolean;
}

export interface CastValidationState {
  [section: string]: Array<ValidationResult & { index: number }>;
}

export const distributorValidationSchema: Record<string, FieldValidationSchema> = {
  Worldwide: {
    required: ['name'],
    optional: ['terr_minimums', 'contact', 'format'],
    rules: {
      name: { min: 1, max: 100 },
    },
  },
  'North America': {
    required: ['name', 'country'],
    optional: ['terr_minimums', 'contact', 'format'],
    rules: {
      name: { min: 1, max: 100 },
    },
  },
  UK: {
    required: ['name', 'country'],
    optional: ['terr_minimums', 'contact', 'format'],
    rules: {
      name: { min: 1, max: 100 },
    },
  },
  Europe: {
    required: ['name', 'country'],
    optional: ['terr_minimums', 'contact', 'format'],
    rules: {
      name: { min: 1, max: 100 },
    },
  },
  'Far East': {
    required: ['name', 'country'],
    optional: ['terr_minimums', 'contact', 'format'],
    rules: {
      name: { min: 1, max: 100 },
    },
  },
  'Latin America': {
    required: ['name', 'country'],
    optional: ['terr_minimums', 'contact', 'format'],
    rules: {
      name: { min: 1, max: 100 },
    },
  },
  'Central America': {
    required: ['name', 'country'],
    optional: ['terr_minimums', 'contact', 'format'],
    rules: {
      name: { min: 1, max: 100 },
    },
  },
  'Middle East': {
    required: ['name', 'country'],
    optional: ['terr_minimums', 'contact', 'format'],
    rules: {
      name: { min: 1, max: 100 },
    },
  },
  'Eastern Europe': {
    required: ['name', 'country'],
    optional: ['terr_minimums', 'contact', 'format'],
    rules: {
      name: { min: 1, max: 100 },
    },
  },
};

export const castValidationSchema: Record<string, FieldValidationSchema> = {
  actors: {
    required: ['name', 'type'],
    optional: ['character'],
    rules: {
      name: { min: 1, max: 100 },
      type: { enum: constants.actorTypes },
    },
  },
  producers: {
    required: ['name', 'role'],
    optional: ['previous_works'],
    rules: {
      name: { min: 1, max: 100 },
      role: { enum: constants.producerRoles },
    },
  },
  directors: {
    required: ['name'],
    optional: ['known_for'],
    rules: {
      name: { min: 1, max: 100 },
    },
  },
  writers: {
    required: ['name'],
    optional: ['previous_works'],
    rules: {
      name: { min: 1, max: 100 },
    },
  },
  editors: {
    required: ['name'],
    optional: ['previous_works', 'role'],
    rules: {
      name: { min: 1, max: 100 },
    },
  },
  casting_directors: {
    required: ['name'],
    optional: ['previous_works'],
    rules: {
      name: { min: 1, max: 100 },
    },
  },
  cinematographers: {
    required: ['name'],
    optional: ['previous_works'],
    rules: {
      name: { min: 1, max: 100 },
    },
  },
  composers: {
    required: ['name'],
    optional: ['previous_works'],
    rules: {
      name: { min: 1, max: 100 },
    },
  },
  production_designers: {
    required: ['name'],
    optional: ['previous_works'],
    rules: {
      name: { min: 1, max: 100 },
    },
  },
  art_directors: {
    required: ['name'],
    optional: ['previous_works'],
    rules: {
      name: { min: 1, max: 100 },
    },
  },
  animators: {
    required: ['name'],
    optional: ['role_credit'],
    rules: {
      name: { min: 1, max: 100 },
    },
  },
};

export const creditsValidationSchema: Record<string, FieldValidationSchema> = {
  production_companies: {
    required: ['company'],
    optional: ['location', 'contact'],
    rules: {
      company: { min: 1, max: 100 },
    },
  },
  special_effects: {
    required: ['company'],
    optional: ['location', 'contact'],
    rules: {
      company: { min: 1, max: 100 },
    },
  },
  additional_companies: {
    required: ['company'],
    optional: ['location', 'contact', 'role'],
    rules: {
      company: { min: 1, max: 100 },
    },
  },
};

export const validateCastItem = (
  item: any,
  sectionName: string,
): ValidationResult => {
  const schema = castValidationSchema[sectionName];
  if (!schema)
    return {
      isValid: true,
      errors: [],
      hasPartialData: false,
      hasAnyValue: false,
    };

  const errors: string[] = [];
  const allFields = schema.required.concat(schema.optional);
  const hasAnyValue = allFields.some((field) => {
    const value = item[field];
    return (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      String(value).trim() !== ''
    );
  });

  // Only validate required fields if item has any field filled
  if (hasAnyValue) {
    schema.required.forEach((field) => {
      if (!item[field] || !String(item[field]).trim()) {
        errors.push(`${field} is required`);
      }
    });

    // Validate field rules
    Object.keys(schema.rules).forEach((field) => {
      const rule = schema.rules[field];
      const value = item[field];

      if (value && rule.enum && !rule.enum.includes(value)) {
        errors.push(`${field} must be one of: ${rule.enum.join(', ')}`);
      }

      if (value && rule.min && String(value).trim().length < rule.min) {
        errors.push(`${field} must be at least ${rule.min} characters`);
      }

      if (value && rule.max && String(value).trim().length > rule.max) {
        errors.push(`${field} must be at most ${rule.max} characters`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    hasPartialData: hasAnyValue && errors.length > 0,
    hasAnyValue,
  };
};

export const getCastValidationState = (castData: any): CastValidationState => {
  const validationState: CastValidationState = {};

  Object.keys(castValidationSchema).forEach((section) => {
    const items = castData?.[section] || [];
    validationState[section] = items.map((item: any, index: number) => ({
      ...validateCastItem(item, section),
      index,
    }));
  });

  return validationState;
};

export const validateDistributorItem = (
  item: any,
  regionName: string,
): ValidationResult => {
  const schema = distributorValidationSchema[regionName];
  if (!schema)
    return {
      isValid: true,
      errors: [],
      hasPartialData: false,
      hasAnyValue: false,
    };

  const errors: string[] = [];
  const allFields = schema.required.concat(schema.optional);
  const hasAnyValue = allFields.some((field) => {
    const value = item[field];
    return (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      String(value).trim() !== ''
    );
  });

  // Only validate required fields if item has any field filled
  if (hasAnyValue) {
    schema.required.forEach((field) => {
      if (!item[field] || !String(item[field]).trim()) {
        errors.push(`${field} is required`);
      }
    });

    // Validate field rules
    Object.keys(schema.rules).forEach((field) => {
      const rule = schema.rules[field];
      const value = item[field];

      if (value && rule.enum && !rule.enum.includes(value)) {
        errors.push(`${field} must be one of: ${rule.enum.join(', ')}`);
      }

      if (value && rule.min && String(value).trim().length < rule.min) {
        errors.push(`${field} must be at least ${rule.min} characters`);
      }

      if (value && rule.max && String(value).trim().length > rule.max) {
        errors.push(`${field} must be at most ${rule.max} characters`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    hasPartialData: hasAnyValue && errors.length > 0,
    hasAnyValue,
  };
};

export const getDistributorFieldValidation = (
  item: any,
  regionName: string,
  fieldName: string,
): FieldValidation => {
  const schema = distributorValidationSchema[regionName];
  if (!schema)
    return {
      isRequired: false,
      isEmpty: true,
      showError: false,
      hasAnyValue: false,
    };

  const allFields = schema.required.concat(schema.optional);
  const hasAnyValue = allFields.some((field) => {
    const value = item[field];
    return (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      String(value).trim() !== ''
    );
  });

  const isRequired = hasAnyValue && schema.required.includes(fieldName);
  const currentFieldValue = item[fieldName];
  const isEmpty =
    currentFieldValue === null ||
    currentFieldValue === undefined ||
    currentFieldValue === '' ||
    String(currentFieldValue).trim() === '';

  return {
    isRequired,
    isEmpty,
    showError: isRequired && isEmpty,
    hasAnyValue,
  };
};

export const getFieldValidation = (
  item: any,
  sectionName: string,
  fieldName: string,
): FieldValidation => {
  const schema = castValidationSchema[sectionName];
  if (!schema)
    return {
      isRequired: false,
      isEmpty: true,
      showError: false,
      hasAnyValue: false,
    };

  const allFields = schema.required.concat(schema.optional);
  const hasAnyValue = allFields.some((field) => {
    const value = item[field];
    return (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      String(value).trim() !== ''
    );
  });

  const isRequired = hasAnyValue && schema.required.includes(fieldName);
  const currentFieldValue = item[fieldName];
  const isEmpty =
    currentFieldValue === null ||
    currentFieldValue === undefined ||
    currentFieldValue === '' ||
    String(currentFieldValue).trim() === '';

  return {
    isRequired,
    isEmpty,
    showError: isRequired && isEmpty,
    hasAnyValue,
  };
};

export const validateCreditsItem = (
  item: any,
  sectionName: string,
): ValidationResult => {
  const schema = creditsValidationSchema[sectionName];
  if (!schema)
    return {
      isValid: true,
      errors: [],
      hasPartialData: false,
      hasAnyValue: false,
    };

  const errors: string[] = [];
  const allFields = schema.required.concat(schema.optional);
  const hasAnyValue = allFields.some((field) => {
    const value = item[field];
    return (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      String(value).trim() !== ''
    );
  });

  if (hasAnyValue) {
    schema.required.forEach((field) => {
      if (!item[field] || !String(item[field]).trim()) {
        errors.push(`${field} is required`);
      }
    });

    Object.keys(schema.rules).forEach((field) => {
      const rule = schema.rules[field];
      const value = item[field];

      if (value && rule.enum && !rule.enum.includes(value)) {
        errors.push(`${field} must be one of: ${rule.enum.join(', ')}`);
      }

      if (value && rule.min && String(value).trim().length < rule.min) {
        errors.push(`${field} must be at least ${rule.min} characters`);
      }

      if (value && rule.max && String(value).trim().length > rule.max) {
        errors.push(`${field} must be at most ${rule.max} characters`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    hasPartialData: hasAnyValue && errors.length > 0,
    hasAnyValue,
  };
};

export const getCreditsFieldValidation = (
  item: any,
  sectionName: string,
  fieldName: string,
): FieldValidation => {
  const schema = creditsValidationSchema[sectionName];
  if (!schema)
    return {
      isRequired: false,
      isEmpty: true,
      showError: false,
      hasAnyValue: false,
    };

  const allFields = schema.required.concat(schema.optional);
  const hasAnyValue = allFields.some((field) => {
    const value = item[field];
    return (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      String(value).trim() !== ''
    );
  });

  const isRequired = hasAnyValue && schema.required.includes(fieldName);
  const currentFieldValue = item[fieldName];
  const isEmpty =
    currentFieldValue === null ||
    currentFieldValue === undefined ||
    currentFieldValue === '' ||
    String(currentFieldValue).trim() === '';

  return {
    isRequired,
    isEmpty,
    showError: isRequired && isEmpty,
    hasAnyValue,
  };
};

// Release dates validation
export const releaseDatesValidationSchema: FieldValidationSchema = {
  required: ['country', 'date'],
  optional: [],
  rules: {},
};

export const validateReleaseDateItem = (item: any): ValidationResult => {
  const schema = releaseDatesValidationSchema;
  const errors: string[] = [];
  const allFields = schema.required.concat(schema.optional);
  const hasAnyValue = allFields.some((field) => {
    const value = item[field];
    return (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      String(value).trim() !== ''
    );
  });

  // Only validate required fields if item has any field filled
  if (hasAnyValue) {
    schema.required.forEach((field) => {
      if (!item[field] || !String(item[field]).trim()) {
        errors.push(`${field} is required`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    hasPartialData: hasAnyValue && errors.length > 0,
    hasAnyValue,
  };
};
